import { Injectable } from '@angular/core';
import { API_END_POINT, SOCIAL_URL } from '@app/core/constants/app-constant';
import { StorageConstants } from '@app/core/constants/storage-constants';
import { ApiService } from '@app/core/http/api.service';
import { ApiResponse } from '@app/shared/models/ApiResponse';
import { AuthProviders, AuthProvidersTypes } from '@app/shared/models/AuthProviders';
import { LoginAccount } from '@app/shared/models/loginAccount';
import { SsoConfirmResp } from '@app/shared/models/ssoConfirmResp';
import { InAppBrowser } from '@app/core/services/in-app-browser/in-app-browser.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { SharedService } from '../shared/shared.service';
import { StorageService } from '../storage/storage.service';
import { ToastService } from '../toast/toast.service';
import { IN_APP_BROWSER_OPT } from '@app/core/constants/app-config-restriction-keys';
import { Capacitor } from '@capacitor/core';
import { environment } from '@env';

@Injectable({
  providedIn: 'root',
})
export class LoginAccountService {
  private readonly accountsKey = 'accounts';
  private readonly accountOnlyCheck = 'accountOnlyCheck';
  defaultAccount$: BehaviorSubject<LoginAccount> = new BehaviorSubject(null);
  authenticatedAccount$: BehaviorSubject<LoginAccount[]> = new BehaviorSubject([]);
  currentLocAtionAuthenticatedAccount$: BehaviorSubject<LoginAccount[]> = new BehaviorSubject([]);
  autoAccountAvail$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  hashId = '';

  constructor(
    private storageService: StorageService,
    private apiService: ApiService,
    private sharedSvc: SharedService,
    private storageConstants: StorageConstants,
    private inAppBrowser: InAppBrowser,
    private toastService: ToastService,
  ) {
    this.setDefaultAccount();
    this.handleAutoAccount();
  }

  handleAutoAccount() {
    this.authenticatedAccount$.subscribe((accounts: LoginAccount[]) => {
      const autoAvail = accounts.some((account) => account.auto);
      if (autoAvail) {
        this.sharedSvc.accountOnly = true;
        const accountOnlyEle: any = document.getElementById('accountOnly');
        if (accountOnlyEle) {
          accountOnlyEle.checked = true;
          accountOnlyEle.disabled = true;
        }
      }
      this.autoAccountAvail$.next(autoAvail);
    });
  }

  async setDefaultAccount(accountList?) {
    let accounts = accountList ?? (await this.read()) ?? [];
    const currentLocationID = (await this.sharedSvc.getCustomerInfo())?.customerData?._id || null;
    const currentLocationAccounts = accounts.filter((account) => {
      return account.ssoConfirmationResp?.user?.CustomerID === currentLocationID;
    });
    const defaultAccount = accounts.find((account) => {
      return account.default && account.ssoConfirmationResp?.user?.CustomerID === currentLocationID;
    });
    this.defaultAccount$.next(defaultAccount);
    const authenticateAccount = accounts?.length
      ? accounts.filter((e) => e.ssoConfirmationResp)
      : [];
    this.authenticatedAccount$.next(authenticateAccount);
    this.currentLocAtionAuthenticatedAccount$.next(
      currentLocationAccounts?.length
        ? currentLocationAccounts.filter((e) => e.ssoConfirmationResp)
        : [],
    );
  }

  getDefaultAccount(): Observable<LoginAccount> {
    return this.defaultAccount$;
  }

  async getAccountById(id: number): Promise<LoginAccount> {
    let accounts = (await this.read()) || [];
    return accounts.find((data) => data.id === id);
  }

  async getActivatedAccount(): Promise<LoginAccount> {
    let accounts = (await this.read()) || [];
    return accounts.find((data) => data.activeAccount);
  }

  public async read(): Promise<LoginAccount[]> {
    let accounts: Array<LoginAccount> = [];
    accounts = await this.storageService.getJson(this.accountsKey);
    return accounts;
  }
  async getAuthenticatedAccount(): Promise<LoginAccount[]> {
    const lstAccounts = await this.read();
    return lstAccounts?.length ? lstAccounts.filter((e) => e.ssoConfirmationResp) : [];
  }

  public async create(account: LoginAccount, rememberMe: boolean) {
    account.id = Date.now();
    return new Promise(async (resolve, rejects) => {
      let dataSet = (await this.read()) ?? [];
      const currentLocationID = (await this.sharedSvc.getCustomerInfo())?.customerData?._id || null;
      const existIndex = dataSet.findIndex(
        (d) => d.account === account.account && d.currentLocationID === account.currentLocationID,
      );
      let defaultIndex = 0;
      if (existIndex > -1 && rememberMe) {
        defaultIndex = existIndex;
      } else {
        dataSet.push(account);
        defaultIndex = dataSet.length - 1;
      }

      if (account.default === true) {
        await this.updateDefault(dataSet, defaultIndex);
      }
      this.authenticatedAccount$.next(
        dataSet?.length ? dataSet.filter((e) => e.ssoConfirmationResp) : [],
      );
      const currentLocationAccounts = dataSet.filter((account) => {
        return account.ssoConfirmationResp?.user?.CustomerID === currentLocationID;
      });
      this.currentLocAtionAuthenticatedAccount$.next(
        currentLocationAccounts?.length
          ? currentLocationAccounts.filter((e) => e.ssoConfirmationResp)
          : [],
      );
      await this.storageService.setJson(this.accountsKey, dataSet);
      return resolve(true);
    });
  }

  async removeRememberAccount(account: LoginAccount) {
    const dataSet = (await this.read()) ?? [];
    const index = dataSet.findIndex(
      (d) => d.account === account.account && d.currentLocationID === account.currentLocationID,
    );
    if (index > -1) {
      dataSet.splice(index, 1);
      await this.storageService.setJson(this.accountsKey, dataSet);
    }
  }

  public async updateSingleRow(account: LoginAccount, index: number) {
    return new Promise(async (resolve, rejects) => {
      let dataSet = (await this.read()) ?? [];

      if (account.default === true) {
        this.removeDefault(dataSet);
      }
      dataSet[index] = account;

      await this.storageService.setJson(this.accountsKey, dataSet);
      return resolve(true);
    });
  }

  public async update(accounts: Array<LoginAccount>) {
    this.setDefaultAccount(accounts);
    return await this.storageService.setJson(this.accountsKey, accounts);
  }

  public async deleteAccount(account: LoginAccount) {
    let accounts = (await this.read()) ?? [];
    accounts = accounts.filter((acc) => acc.id !== account.id);
    this.setDefaultAccount(accounts);
    return await this.storageService.setJson(this.accountsKey, accounts);
  }

  public async delete() {
    return await this.storageService.remove(this.accountsKey);
  }
  public async updateDefault(
    accounts: LoginAccount[],
    newDefaultIndex: number,
  ): Promise<Array<LoginAccount>> {
    this.removeDefault(accounts);
    accounts[newDefaultIndex].default = true;
    return await this.update(accounts);
  }
  public async setAccountOnly(value: boolean) {
    await this.storageService.setJson(this.accountOnlyCheck, { value });
  }
  public async getAccountOnly() {
    return await this.storageService.get(this.accountOnlyCheck);
  }
  private removeDefault(dataSet) {
    const currentLocationID = this.sharedSvc.getCustomerDetails.customerData._id || null;
    const oldDefaultIndex = dataSet.findIndex(
      (acc) => acc.default === true && acc.currentLocationID === currentLocationID,
    );
    if (oldDefaultIndex != -1) {
      dataSet[oldDefaultIndex].default = false;
    }
  }
  async saveAccount(
    formControl: any,
    isDefault: boolean,
    ssoConfirmResp: SsoConfirmResp,
    lstAuthProviders: Array<AuthProviders>,
    provider: string,
    editIndex?: number,
    authPro?: string,
  ) {
    const customerName = formControl.customerName.value;
    const account = formControl.account.value;
    const desc = formControl.description.value;
    const providerIndex = formControl.authProvider.value;
    const providerName =
      provider || (providerIndex !== '' ? lstAuthProviders[providerIndex].ProviderName : '');
    const authProvider =
      authPro || (providerIndex !== '' ? lstAuthProviders[providerIndex]?.AuthProvider : '');
    let accountsData: LoginAccount;
    const currentLocationID = this.sharedSvc.getCustomerDetails.customerData._id || null;
    accountsData = {
      customerName: customerName,
      account: account,
      description: desc,
      default: isDefault,
      providerIndex: providerIndex,
      providerName,
      ssoConfirmationResp: authProvider === '' ? null : ssoConfirmResp,
      authProvider: authProvider,
      currentLocationID,
    };

    if (typeof editIndex !== 'undefined') {
      await this.updateSingleRow(accountsData, editIndex);
    } else {
      await this.create(accountsData, false);
    }
    return null;
  }
  async login(lstAuthProviders: Array<AuthProviders>, indexOfSelectAuth: number): Promise<string> {
    this.hashId = '';
    const authProviders: AuthProviders = lstAuthProviders[indexOfSelectAuth];
    const provider = authProviders.AuthProvider.toLowerCase();
    const authId = authProviders._id;
    if (provider === AuthProvidersTypes.azureAD.toLowerCase()) {
      if (this.autoAccountAvail$?.value) {
        await this.toastService.presentErrorToast('AZURE_ACCOUNT_ADDED_MSG');
        return this.hashId;
      }
    }
    if (Capacitor.isNativePlatform()) {
      return new Promise((resolve, reject) => {
        const browser = this.inAppBrowser.create(
          SOCIAL_URL.azureLogin(authId, authProviders.OrgID, provider),
          '_self',
          IN_APP_BROWSER_OPT,
        );
        const loadStartListener = browser.on('loadstart').subscribe(async (e) => {
          if (e?.url && e?.url.includes(environment.redirectBaseUrl)) {
            const rawUrl = e.url;
            const lowerUrl = rawUrl.toLowerCase();

            const providers = [
              AuthProvidersTypes.gsuite,
              AuthProvidersTypes.saml,
              AuthProvidersTypes.oidc,
              AuthProvidersTypes.azureAD,
            ].map((p) => p.toLowerCase());

            const matchedProvider = providers.find((provider) => lowerUrl.includes(provider));
            if (!matchedProvider) {
              this.toastService.presentErrorToast();
            } else {
              this.hashId =
                new URL(rawUrl).searchParams.get('hashId') ||
                new URL(rawUrl).searchParams.get('hashid');
              if (!this.hashId) {
                this.toastService.presentErrorToast();
              }
            }
            loadStartListener.unsubscribe();
            await browser.close();
          }
        });
        browser.on('exit').subscribe(() => {
          loadStartListener?.unsubscribe();
          resolve(this.hashId);
        });
      });
    } else {
      this.inAppBrowser.create(
        SOCIAL_URL.azureLogin(authId, authProviders.OrgID, provider),
        '_system',
        {},
      );
      return '';
    }
  }

  async ssoConfirm(hashId: string, authId: string): Promise<ApiResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.sharedSvc.getCustomerDetails) {
          this.sharedSvc.getCustomerDetails = await this.sharedSvc.getCustomerInfo();
        }

        // const result = await this.apiService.post('sso/confirm', {
        const result = await this.apiService.post(API_END_POINT.ssoConfirm, {
          hashId,
          authId,
        });
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
  }
  saveFormState(formControl: any, isDefault: boolean, rowEditIndex: number) {
    //only for web
    const customerName = formControl.customerName.value;
    const account = formControl.account.value;
    const desc = formControl.description.value;
    const authProvider = formControl.authProvider.value;

    let data: LoginAccount = {
      customerName: customerName,
      account: account,
      description: desc,
      default: isDefault,
      providerIndex: authProvider,
      providerName: '',
      rowEditIndex: rowEditIndex,
    };

    this.storageService.setJson(this.storageConstants.INPROGRESS_ADD_ACCOUNT_FORM, data);
  }

  async getCurrentLocationAccount() {
    const AllAccounts = await this.getAuthenticatedAccount();
    return (
      AllAccounts?.filter((e) => {
        const selectedAccountID = e.ssoConfirmationResp.user.CustomerID || null;
        const currentLocationID = this.sharedSvc.getCustomerDetails.customerData._id || null;
        return selectedAccountID === currentLocationID;
      }) || []
    );
  }

  async getCurrentAccount() {
    const AllAccounts = await this.read();
    return (
      AllAccounts?.filter((e) => {
        const ssoSelectedAccountID = e.ssoConfirmationResp?.user?.CustomerID || null;
        const selectedAccountID = e.currentLocationID || null;
        const currentLocationID = this.sharedSvc.getCustomerDetails.customerData._id || null;
        return (
          (ssoSelectedAccountID && ssoSelectedAccountID === currentLocationID) ||
          (selectedAccountID && selectedAccountID === currentLocationID)
        );
      }) || []
    );
  }
}
